import React, { useEffect, useState } from "react";
import { TrendingUp, Banknote, Users, Activity } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard, RiskBadge } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { ClinicalBanner } from "@/components/Disclaimer";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function Predictive() {
  const [det, setDet] = useState([]);
  const [cd, setCD] = useState([]);
  const [pa, setPA] = useState([]);
  const [rev, setRev] = useState(null);
  const [to, setTO] = useState([]);

  useEffect(() => {
    api.get("/predictive/deterioration").then(r=>setDet(r.data));
    api.get("/predictive/claim-denial").then(r=>setCD(r.data));
    api.get("/predictive/pa-rejection").then(r=>setPA(r.data));
    api.get("/predictive/revenue-forecast").then(r=>setRev(r.data));
    api.get("/predictive/staff-turnover").then(r=>setTO(r.data));
  }, []);

  const detCols = [
    { key: "patient_name", label: "Patient" },
    { key: "risk", label: "Risk", render: (r)=><RiskBadge risk={r.risk} /> },
    { key: "probability_72h", label: "Prob 72h", render: (r)=>`${(r.probability_72h*100).toFixed(0)}%` },
    { key: "primary_factor", label: "Driver" },
    { key: "recommended_action", label: "Action" },
  ];

  const cdCols = [
    { key: "claim_number", label: "Claim #" },
    { key: "payer", label: "Payer" },
    { key: "denial_probability", label: "Denial Prob", render: (r)=><span className={r.denial_probability>0.5?"text-red-600 font-semibold":"text-amber-600"}>{(r.denial_probability*100).toFixed(0)}%</span> },
    { key: "predicted_reason", label: "Predicted Reason" },
  ];

  const paCols = [
    { key: "procedure", label: "Procedure" },
    { key: "payer", label: "Payer" },
    { key: "rejection_probability", label: "Reject Prob", render: (r)=>`${(r.rejection_probability*100).toFixed(0)}%` },
    { key: "weak_arguments", label: "Weak Arguments" },
  ];

  const toCols = [
    { key: "name", label: "Staff" },
    { key: "role", label: "Role" },
    { key: "turnover_risk", label: "Risk", render: (r)=>`${(r.turnover_risk*100).toFixed(0)}%` },
    { key: "level", label: "Level", render: (r)=><RiskBadge risk={r.level} /> },
    { key: "drivers", label: "Drivers" },
  ];

  return (
    <>
      <PageHeader eyebrow="Module 15" title="Predictive Clinical Intelligence"
        subtitle="Forward-looking AI: deterioration risk, denial prediction, revenue forecast and staff turnover." testid="predictive-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <KpiCard icon={Banknote} label="30-day Revenue Forecast" value={rev?`$${(rev.next_30_days/1000).toFixed(0)}k`:"—"} hint={rev?`${rev.confidence_pct}% confidence`:""} testid="kpi-rev30" />
        <KpiCard icon={TrendingUp} label="60-day" value={rev?`$${(rev.next_60_days/1000).toFixed(0)}k`:"—"} testid="kpi-rev60" />
        <KpiCard icon={TrendingUp} label="90-day" value={rev?`$${(rev.next_90_days/1000).toFixed(0)}k`:"—"} testid="kpi-rev90" />
        <KpiCard icon={Users} label="High-risk patients" value={det.filter(d=>d.risk==="high"||d.risk==="critical").length} accent testid="kpi-highrisk" />
      </div>

      {rev && (
        <SectionCard title="Revenue Forecast (4-week)" testid="rev-forecast">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={rev.by_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E9E3F5" />
              <XAxis dataKey="week" tick={{ fontSize: 11, fill: "#6E6380" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6E6380" }} tickFormatter={(v)=>`$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v)=>`$${v.toLocaleString()}`} />
              <Area type="monotone" dataKey="high" stroke="#9B59B6" fillOpacity={0.15} fill="#9B59B6" />
              <Area type="monotone" dataKey="forecast" stroke="#4A0E8F" strokeWidth={2.5} fillOpacity={0.35} fill="#4A0E8F" />
              <Area type="monotone" dataKey="low" stroke="#9B59B6" fillOpacity={0.15} fill="#9B59B6" />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="Patient Deterioration (72h)" testid="det-section">
          <DataTable data={det} columns={detCols} searchKeys={["patient_name","risk"]} testid="det-table" />
        </SectionCard>
        <SectionCard title="Claim Denial Prediction" testid="cd-section">
          <DataTable data={cd} columns={cdCols} searchKeys={["claim_number","payer"]} testid="cd-table" />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <SectionCard title="PA Rejection Prediction" testid="pa-pred-section">
          <DataTable data={pa} columns={paCols} searchKeys={["procedure"]} testid="pa-pred-table" />
        </SectionCard>
        <SectionCard title="Staff Turnover Risk" testid="turnover-section">
          <DataTable data={to} columns={toCols} searchKeys={["name","role"]} testid="turnover-table" />
        </SectionCard>
      </div>
    </>
  );
}
